import { HEALTH_SCOPE_STATUS } from './healthScopes';
import { buildScansPathWithFilter, pageToPath } from '../../routes/dashboardRoutes';

export const CAPABILITY_STATE = Object.freeze({
  SCORED: 'scored',
  INSUFFICIENT: 'insufficient_data',
  NOT_LICENSED: 'not_licensed',
  NOT_CONFIGURED: 'not_configured',
});

export const CAPABILITY_STATE_LABEL = Object.freeze({
  [CAPABILITY_STATE.SCORED]: 'Active',
  [CAPABILITY_STATE.INSUFFICIENT]: 'Not Active',
  [CAPABILITY_STATE.NOT_LICENSED]: 'Not Active',
  [CAPABILITY_STATE.NOT_CONFIGURED]: 'Not Active',
});

/** Coverage layers shown on Security Overview (backend health v1.2 scope keys). */
export const COVERAGE_LAYERS = Object.freeze([
  {
    key: 'external',
    title: 'Internet Perimeter',
    description: 'Public IPs, firewalls and internet-facing services.',
  },
  {
    key: 'network',
    title: 'Attack Surface',
    description: 'Domains, exposed assets and external services.',
  },
  {
    key: 'web',
    title: 'Web Application Security',
    description: 'Websites, portals &\ncustomer-facing applications.',
  },
  {
    key: 'internal',
    title: 'Internal Network Security',
    description: 'Servers, endpoints and internal infrastructure.',
  },
]);

export function getCapabilityState(scopeKey, scope) {
  if (scope?.status === HEALTH_SCOPE_STATUS.READY) return CAPABILITY_STATE.SCORED;
  const diagnostics = scope?.diagnostics || null;
  const entitled = diagnostics?.entitled;
  const configured = diagnostics?.configured;

  if (scope?.applicable === false) {
    if (entitled === false) return CAPABILITY_STATE.NOT_LICENSED;
    if (scopeKey === 'internal' && entitled === true) return CAPABILITY_STATE.NOT_CONFIGURED;
    if (configured === false) return CAPABILITY_STATE.NOT_CONFIGURED;
    return CAPABILITY_STATE.NOT_LICENSED;
  }

  if (entitled === true && configured === false) {
    return CAPABILITY_STATE.NOT_CONFIGURED;
  }
  return CAPABILITY_STATE.INSUFFICIENT;
}

export function scopeAction(scopeKey, capabilityState) {
  if (capabilityState === CAPABILITY_STATE.NOT_LICENSED) {
    if (scopeKey === 'web') {
      return { label: 'Unlock Web Security', href: pageToPath('subscription'), primary: true };
    }
    if (scopeKey === 'internal') {
      return { label: 'Unlock Internal Security', href: pageToPath('subscription'), primary: true };
    }
    return {
      label: 'Explore plans',
      href: pageToPath('subscription'),
      primary: true,
    };
  }

  if (capabilityState === CAPABILITY_STATE.NOT_CONFIGURED) {
    return {
      label: 'Set up',
      href: pageToPath('settingsConnectors'),
      primary: true,
    };
  }

  if (capabilityState === CAPABILITY_STATE.INSUFFICIENT) {
    if (scopeKey === 'web') {
      return { label: 'Run a scan', href: buildScansPathWithFilter({ tab: 'web' }), primary: true };
    }
    if (scopeKey === 'internal') {
      return {
        label: 'View scans',
        href: buildScansPathWithFilter({ tab: 'network', location: 'onprem' }),
        primary: false,
      };
    }
    if (scopeKey === 'external') {
      return {
        label: 'View Details',
        href: buildScansPathWithFilter({ tab: 'network', location: 'perimeter' }),
        primary: false,
      };
    }
    return {
      label: 'View Details',
      href: buildScansPathWithFilter({ tab: 'network' }),
      primary: false,
    };
  }

  return {
    label: 'View Details',
    href:
      scopeKey === 'web'
        ? buildScansPathWithFilter({ tab: 'web' })
        : scopeKey === 'internal'
          ? buildScansPathWithFilter({ tab: 'network', location: 'onprem' })
          : scopeKey === 'external'
            ? buildScansPathWithFilter({ tab: 'network', location: 'perimeter' })
            : buildScansPathWithFilter({ tab: 'network' }),
    primary: false,
  };
}

/**
 * Active / total coverage layers from health scopes (presentation only).
 * "Active" means the scope currently has a ready score.
 */
export function summarizeCoverageLayers(scopes) {
  const layers = COVERAGE_LAYERS.map((layer) => {
    const scope = scopes?.[layer.key] || null;
    const capabilityState = getCapabilityState(layer.key, scope);
    const ready = scope?.status === HEALTH_SCOPE_STATUS.READY;
    return {
      ...layer,
      scope,
      capabilityState,
      ready,
      grade: ready && scope?.grade ? String(scope.grade).trim() : null,
      score: ready && scope?.score != null ? Math.round(scope.score) : null,
      action: scopeAction(layer.key, capabilityState),
      statusLabel: CAPABILITY_STATE_LABEL[capabilityState] || 'Not Monitored',
    };
  });
  const activeCount = layers.filter((layer) => layer.ready).length;
  const totalCount = layers.length;
  const coveragePct = totalCount > 0 ? Math.round((activeCount / totalCount) * 100) : 0;
  const inactiveTitles = layers.filter((layer) => !layer.ready).map((layer) => layer.title);
  return { layers, activeCount, totalCount, coveragePct, inactiveTitles };
}

const LAYER_SCAN_LABEL = Object.freeze({
  'Internet Perimeter': 'perimeter scanning',
  'Attack Surface': 'attack surface scanning',
  'Web Application Security': 'web application scanning',
  'Internal Network Security': 'internal network scanning',
});

function formatAddScanningPhrase(inactiveTitles) {
  const labels = (inactiveTitles || [])
    .map((title) => LAYER_SCAN_LABEL[title] || String(title || '').toLowerCase())
    .filter(Boolean);
  if (labels.length === 0) return 'more scanning layers';
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

/** Mockup-aligned coverage banner copy (no invented scores; based on inactive layers). */
export function coverageCalloutCopy(summary) {
  const { coveragePct, inactiveTitles, activeCount, totalCount } = summary;
  if (activeCount >= totalCount) {
    return {
      coveragePct,
      body: 'Your score reflects the security evidence currently available across all assessed layers.',
    };
  }
  if (activeCount === 0) {
    return {
      coveragePct,
      body: 'Your score reflects the security evidence currently available. Enable scanning layers for a more complete and accurate view of your security posture.',
    };
  }
  return {
    coveragePct,
    body: `Your score reflects the security evidence currently available. Add ${formatAddScanningPhrase(inactiveTitles)} for a more complete and accurate view of your security posture.`,
  };
}
